import { adminService } from '../services/api';

/**
 * Placements and employee/customer status are managed in CRM (Tempus 1.70.0).
 * The backend answers GET /admin/assignments/policy with whether the local
 * actions are switched on (TEMPUS_LOCAL_ASSIGNMENT_ACTIONS_ENABLED) and where
 * in CRM they are managed instead. The pages fail closed: while the policy is
 * loading, or when it cannot be read, the local actions stay hidden, because
 * the server refuses them by default anyway.
 */
export const READ_ONLY_POLICY = Object.freeze({ localActionsEnabled: false, managedIn: 'crm', crm: null, unavailable: true });
export const MANAGE_IN_CRM_LABEL = 'Manage placements in CRM';

export const loadAssignmentPolicy = async () => {
  try {
    const data = await adminService.getAssignmentPolicy();
    if (!data || typeof data.localActionsEnabled !== 'boolean') return READ_ONLY_POLICY;
    return {
      localActionsEnabled: data.localActionsEnabled,
      managedIn: data.managedIn || (data.localActionsEnabled ? 'tempus' : 'crm'),
      crm: data.crm && typeof data.crm === 'object' ? data.crm : null,
      unavailable: false,
    };
  } catch (error) {
    return READ_ONLY_POLICY;
  }
};

export const localActionsAllowed = (policy) => Boolean(policy && policy.localActionsEnabled);

export const crmEmployeesHref = (policy) => (policy && policy.crm && policy.crm.employeesUrl) || null;

/** Today on the company's clock (Europe/Copenhagen), as the server counts it. */
export const companyToday = (now = new Date()) => now.toLocaleDateString('en-CA', { timeZone: 'Europe/Copenhagen' });

export const CORRECTION_MESSAGES = Object.freeze({
  adds_end_date: 'Ending employment is managed in CRM. A date correction cannot add a last employment day to an open-ended placement.',
  removes_end_date: 'Reopening employment is managed in CRM. A date correction cannot remove the last employment day.',
  changes_current_employment: 'This correction would start or end employment today. Make that change in CRM; Tempus records date corrections only.',
});

const employmentPhase = (startDate, endDate, today) => {
  if (endDate && endDate < today) return 'ended';
  return startDate > today ? 'planned' : 'current';
};

/**
 * Mirrors the server rule (Tempus 1.70.1). With employment managed in CRM a
 * date correction may fix dates only: it may not add or remove the last
 * employment day, and it may not change whether the placement is ended,
 * current or planned today. Returns null when allowed, otherwise the reason.
 */
export const correctionChangesEmployment = (placement, startDate, endDate, today = companyToday()) => {
  if (!placement || !startDate) return null;
  const before = placement.endDate || null;
  const after = endDate || null;
  if (before === null && after !== null) return 'adds_end_date';
  if (before !== null && after === null) return 'removes_end_date';
  return employmentPhase(placement.startDate, before, today) !== employmentPhase(startDate, after, today)
    ? 'changes_current_employment' : null;
};

/** Deep link to the CRM employee page when the employee carries a CRM link, otherwise the CRM employees list. */
export const crmEmployeeHref = (policy, employee) => {
  const crm = policy && policy.crm;
  if (!crm) return null;
  const crmEmployeeId = employee && employee.crmEmployeeId;
  if (crmEmployeeId && crm.employeeUrlTemplate) {
    return crm.employeeUrlTemplate.replace('{crmEmployeeId}', encodeURIComponent(String(crmEmployeeId)));
  }
  return crm.employeesUrl || null;
};
